import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Download, Trash2, Search, ChevronDown, MoonStar, BellRing } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = ["new", "contacted", "qualified", "proposal_sent", "won", "closed", "archived"];
const TYPES = ["", "recruitment", "consulting", "ai_solutions", "speaking", "partnership", "media", "general"];

const STATUS_LABEL = {
  new: "New",
  contacted: "Contacted",
  qualified: "Qualified",
  proposal_sent: "Proposal sent",
  won: "Won",
  closed: "Closed",
  archived: "Archived",
};

const TYPE_LABEL = {
  "": "All types",
  recruitment: "Recruitment",
  consulting: "Consulting",
  ai_solutions: "AI Solutions",
  speaking: "Speaking",
  partnership: "Partnership",
  media: "Media",
  general: "General",
};

export default function AdminEnquiries() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.enquiry_type = typeFilter;
      if (search) params.q = search;
      const r = await axios.get(`${API}/admin/enquiries`, { params, withCredentials: true });
      setItems(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, search]);

  useEffect(() => {
    fetchList();
  }, [statusFilter, typeFilter, fetchList]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/enquiries/${id}`, { status }, { withCredentials: true });
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
      toast.success("Status updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/enquiries/${id}`, { withCredentials: true });
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const unsnooze = async (id) => {
    try {
      await axios.delete(`${API}/admin/enquiries/${id}/snooze`, { withCredentials: true });
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, snoozed_until: null } : i)));
      toast.success("Snooze cleared");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const exportCsv = async () => {
    try {
      const r = await axios.get(`${API}/admin/enquiries/export.csv`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "enquiries.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const filtered = useMemo(() => items, [items]);

  return (
    <div data-testid="admin-enquiries">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow">Inbox</p>
          <h1 className="mt-3 font-display text-4xl md:text-5xl leading-tight tracking-tight">Enquiries</h1>
        </div>
        <button onClick={exportCsv} data-testid="enquiries-export-csv" className="btn-ghost">
          <Download size={14} /> Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="mt-8 flex flex-wrap gap-3 items-center">
        <div className="flex items-center border border-[#343437] px-3 gap-2 bg-[#151517]">
          <Search size={14} className="text-[#A9A9A6]" />
          <input
            data-testid="enquiries-search"
            className="bg-transparent py-2.5 text-sm outline-none w-56 text-[#FAF9F6]"
            placeholder="Search name, email, org…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchList()}
          />
        </div>

        <SelectPill
          testid="enquiries-filter-type"
          value={typeFilter}
          onChange={setTypeFilter}
          options={TYPES.map((v) => ({ value: v, label: TYPE_LABEL[v] }))}
        />
        <SelectPill
          testid="enquiries-filter-status"
          value={statusFilter}
          onChange={setStatusFilter}
          options={[{ value: "", label: "All statuses" }, ...STATUSES.map((s) => ({ value: s, label: STATUS_LABEL[s] }))]}
        />

        <span className="text-xs text-[#A9A9A6] ml-auto">{filtered.length} results</span>
      </div>

      {/* Table */}
      <div className="mt-6 border border-[#343437]">
        <div className="hidden md:grid grid-cols-[1fr_1.4fr_1fr_1fr_140px_60px] gap-0 border-b border-[#343437] bg-[#151517]">
          {["Contact", "Enquiry", "Type", "Received", "Status", ""].map((h) => (
            <div key={h} className="px-4 py-3 text-[10px] tracking-[0.22em] uppercase text-[#A9A9A6]">{h}</div>
          ))}
        </div>

        {(() => {
          if (loading) return <div className="p-8 text-[#A9A9A6] text-sm">Loading…</div>;
          if (filtered.length === 0) {
            return <div className="p-8 text-[#A9A9A6] text-sm" data-testid="enquiries-empty">No enquiries match.</div>;
          }
          return filtered.map((e) => (
            <div
              key={e.id}
              className="border-t border-[#343437] first:border-t-0"
              data-testid={`enquiry-row-${e.id}`}
            >
              <div className="grid grid-cols-1 md:grid-cols-[1fr_1.4fr_1fr_1fr_140px_60px] gap-0">
                <div className="px-4 py-4">
                  <p className="font-display text-[15px] text-[#FAF9F6]">{e.full_name}</p>
                  <p className="text-xs text-[#A9A9A6] break-all">{e.email}</p>
                  {e.organisation && (
                    <p className="text-xs text-[#A9A9A6] mt-1">{e.organisation}</p>
                  )}
                  {e.snoozed_until && new Date(e.snoozed_until) > new Date() && (
                    <button
                      type="button"
                      onClick={() => unsnooze(e.id)}
                      data-testid={`enquiry-unsnooze-${e.id}`}
                      className="mt-2 inline-flex items-center gap-1 text-[10px] tracking-[0.14em] uppercase text-[#D8A936] hover:text-[#FAF9F6]"
                      title="Clear snooze"
                    >
                      <MoonStar size={10} /> Snoozed until {new Date(e.snoozed_until).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
                    </button>
                  )}
                </div>
                <div className="px-4 py-4">
                  <button
                    onClick={() => setExpandedId(expandedId === e.id ? null : e.id)}
                    className="text-left text-sm text-[#FAF9F6] hover:text-[#D8A936] transition-colors line-clamp-2"
                  >
                    {e.message}
                  </button>
                </div>
                <div className="px-4 py-4 text-xs text-[#A9A9A6]">{TYPE_LABEL[e.enquiry_type] || e.enquiry_type}</div>
                <div className="px-4 py-4 text-xs text-[#A9A9A6]">
                  {new Date(e.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                </div>
                <div className="px-4 py-4">
                  <select
                    value={e.status}
                    onChange={(ev) => updateStatus(e.id, ev.target.value)}
                    data-testid={`enquiry-status-${e.id}`}
                    className="w-full bg-[#151517] border border-[#343437] text-xs text-[#FAF9F6] px-2 py-1.5 focus:border-[#D8A936] outline-none"
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                    ))}
                  </select>
                </div>
                <div className="px-4 py-4 flex items-start justify-end">
                  <button
                    onClick={() => remove(e.id)}
                    data-testid={`enquiry-delete-${e.id}`}
                    className="p-2 text-[#A9A9A6] hover:text-red-400 transition-colors"
                    aria-label="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              {expandedId === e.id && (
                <div className="border-t border-[#343437] bg-[#151517] p-6 space-y-4" data-testid={`enquiry-expanded-${e.id}`}>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                    <Field label="Role" value={e.role} />
                    <Field label="Phone" value={e.phone} />
                    <Field label="Country" value={e.country} />
                    <Field label="Timeframe" value={e.timeframe} />
                  </div>
                  <div>
                    <p className="text-[10px] tracking-[0.22em] uppercase text-[#A9A9A6] mb-2">Message</p>
                    <p className="text-sm text-[#FAF9F6] whitespace-pre-wrap leading-relaxed">{e.message}</p>
                  </div>
                  <a href={`mailto:${e.email}`} className="btn-primary !py-2 !text-[11px]">Reply via email</a>
                </div>
              )}
            </div>
          ));
        })()}
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[10px] tracking-[0.22em] uppercase text-[#A9A9A6]">{label}</p>
      <p className="text-[#FAF9F6] mt-1">{value || "—"}</p>
    </div>
  );
}

function SelectPill({ value, onChange, options, testid }) {
  return (
    <label className="relative inline-flex items-center border border-[#343437] bg-[#151517]">
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="appearance-none bg-transparent text-[#FAF9F6] pl-3 pr-8 py-2.5 text-sm outline-none"
      >
        {options.map((o) => (
          <option key={o.value || "all"} value={o.value} className="bg-[#0B0B0C]">
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown size={14} className="absolute right-2 text-[#A9A9A6] pointer-events-none" />
    </label>
  );
}
